// Client helper for the Maya sales-recovery funnel. Fire-and-forget: it never
// blocks the UI and silently no-ops during SSR or when storage is blocked.
import { useCallback, useEffect } from "react";
import { getMayaAnonSessionId } from "@/lib/maya-session";

export type RecoveryStage =
  | "viewed"
  | "interest"
  | "checkout_started"
  | "checkout_error";

export interface RecoveryIntentPayload {
  tourSlug: string;
  tourTitle?: string;
  stage?: RecoveryStage;
  source?: "bokun" | "custom" | "external" | "unknown";
  lang?: string;
  amount?: number;
  currency?: string;
  travelers?: number;
  activityDate?: string;
  email?: string;
  error?: string;
}

export function trackRecoveryIntentClient(payload: RecoveryIntentPayload): void {
  if (typeof window === "undefined") return;
  const sessionKey = getMayaAnonSessionId();
  if (!sessionKey || sessionKey.length < 6 || !payload.tourSlug) return;
  void import("@/lib/recovery-intent.functions")
    .then(({ trackRecoveryIntent }) =>
      trackRecoveryIntent({ data: { sessionKey, stage: payload.stage ?? "viewed", ...payload } }),
    )
    .catch(() => {});
}

export function markRecoveryIntentConvertedClient(tourSlug: string): void {
  if (typeof window === "undefined" || !tourSlug) return;
  const sessionKey = getMayaAnonSessionId();
  if (!sessionKey || sessionKey.length < 6) return;
  void import("@/lib/recovery-intent.functions")
    .then(({ markRecoveryIntentConverted }) =>
      markRecoveryIntentConverted({ data: { sessionKey, tourSlug } }),
    )
    .catch(() => {});
}

/** Records that this browser looked at a tour (once per mount). */
export function useTrackTourView(payload: RecoveryIntentPayload | null): void {
  const slug = payload?.tourSlug ?? "";
  const title = payload?.tourTitle ?? "";
  const source = payload?.source;
  const lang = payload?.lang;
  useEffect(() => {
    if (!slug) return;
    const id = window.setTimeout(
      () => trackRecoveryIntentClient({ tourSlug: slug, tourTitle: title, source, lang, stage: "viewed" }),
      1200,
    );
    return () => window.clearTimeout(id);
  }, [slug, title, source, lang]);
}

export function useRecoveryIntent() {
  return useCallback(
    (payload: RecoveryIntentPayload) => trackRecoveryIntentClient(payload),
    [],
  );
}

/** Marks every open intent of this browser as converted after a purchase. */
export function markRecoverySessionConvertedClient(): void {
  if (typeof window === "undefined") return;
  const sessionKey = getMayaAnonSessionId();
  if (!sessionKey || sessionKey.length < 6) return;
  void import("@/lib/recovery-intent.functions")
    .then(({ markRecoverySessionConverted }) =>
      markRecoverySessionConverted({ data: { sessionKey } }),
    )
    .catch(() => {});
}
